import { useSelector } from 'react-redux';
import { TMutationContext } from './types';
import { selectJWT } from '../../store/selector/selectJWT';
import { selectLocale } from '../../store/selector/selectLocale';
import { useLocation, useParams } from 'react-router-dom';
import { selectCustomStates } from '../../store/selector/selectStates';
import { RootState } from '../../store';
import { selectExpressions } from '../../store/selector/selectExpressions';
import { useMemo } from 'react';
import { selectMe } from '../../store/selector/selectMe';
import actions from '../../utils/actions';
import { selectGlobalConstants } from '../../store/selector/selectGlobalConstants';
import QueryString from 'qs';
import { api } from '../../config';
import * as config from '../../config';
import { constructContexts } from './constructContexts';
import { merge } from 'lodash';

const EMPTY_OBJ = Object.freeze({});

const useContexts = (
  selectors: string[] = [],
  customContexts?: Partial<TMutationContext>
): Partial<TMutationContext> => {
  const me = useSelector((state: RootState) =>
    selectors.includes('me') ? selectMe(state) : undefined
  );
  const jwt = useSelector((state: RootState) =>
    selectors.includes('jwt') ? selectJWT(state) : undefined
  );
  const locale = useSelector((state: RootState) =>
    selectors.includes('locale') ? selectLocale(state) : config.app.default.locale
  );
  const states = useSelector((state: RootState) =>
    selectors.includes('states') ? selectCustomStates(state) : EMPTY_OBJ
  );
  const expressions = useSelector((state: RootState) =>
    selectors.includes('expressions') ? selectExpressions(state) : EMPTY_OBJ
  );
  const constants = useSelector((state: RootState) =>
    selectors.includes('constants') ? selectGlobalConstants(state) : EMPTY_OBJ
  );
  const locationFull = useLocation();
  const paramsFull = useParams();
  const location = selectors.includes('location') ? locationFull : undefined;
  const params = selectors.includes('params') ? paramsFull : undefined;
  const serverHost = api.host;
  const host = document.location.host;

  const staticContext = constructContexts();

  const context = useMemo(
    () =>
      merge(
        staticContext,
        {
          me,
          jwt,
          locale,
          states,
          exp: expressions,
          fn: actions,
          document: {
            serverHost,
            host,
            pathname: location?.pathname,
            params,
            query: location?.search ? QueryString.parse(location.search.substring(1)) : undefined,
          },
          constants,
        } as Partial<TMutationContext>,
        customContexts
      ),
    [
      staticContext,
      me,
      jwt,
      locale,
      states,
      expressions,
      actions,
      serverHost,
      host,
      location?.pathname,
      location?.search,
      customContexts,
    ]
  );

  return context;
};

export default useContexts;
